import { ORDER_BY_CREATED } from "mds/hooks/use-ordered";
import {
  breakoutGroupSelectors,
  classUserSelectors,
  createAppSelector,
  pageGroupSelectors,
} from "store/selectors/base";
import {
  selectActiveClassSession,
  selectIsClassSessionVirtual,
} from "store/selectors/class-session";
import { selectCurrentClassUser, selectCurrentClassUserId } from "store/selectors/class-user";
import { selectUserIdsWhoCanAuthorSection } from "store/selectors/permissions";
import {
  selectIsObservingVirtualClass,
  selectObserverBreakoutGroupId,
  selectObserverListenedBreakoutGroupId,
} from "store/selectors/pubsub-virtual-class-state";
import { filterMap } from "utils/collections";

export const selectIsInBreakout = createAppSelector(
  [selectActiveClassSession],
  (classSession) => !!classSession?.breakout_session_ends_at,
);

export const selectOrderedBreakoutGroups = createAppSelector(
  [
    selectActiveClassSession,
    breakoutGroupSelectors.filterForCurrentSection,
    pageGroupSelectors.selectAll,
  ],
  (classSession, breakout_groups, page_groups) => {
    let breakoutGroups = breakout_groups;

    if (
      !!classSession?.breakout_session_ends_at &&
      !!classSession?.breakout_session_synced_page_id
    ) {
      const syncedPageGroupById: Record<string, (typeof page_groups)[number]> = {};
      for (const item of page_groups
        .filter((pageGroup) => pageGroup.page_id === classSession.breakout_session_synced_page_id)
        .toSorted(ORDER_BY_CREATED)) {
        syncedPageGroupById[item.id] = item;
      }

      breakoutGroups = breakout_groups.map((breakoutGroup) => ({
        ...breakoutGroup,
        created_at_ms:
          syncedPageGroupById[breakoutGroup.page_group_id ?? ""]?.created_at_ms ??
          breakoutGroup.created_at_ms,
      }));
    }

    return breakoutGroups
      .toSorted(ORDER_BY_CREATED)
      .map((bg, index) => ({ ...bg, number: index + 1 }));
  },
);

export const selectFullBreakoutGroups = createAppSelector(
  [selectOrderedBreakoutGroups, classUserSelectors.filterForCurrentSection],
  (breakout_groups, classUsers) => {
    return breakout_groups.map((breakout_group) => {
      return {
        ...breakout_group,
        members: classUsers.filter((cu) => cu.breakout_group_id === breakout_group.id),
      };
    });
  },
);

// Effective room the current user is in: ClassUser join id, else observer local visit
// while observing. Canonical "which breakout am I in?" for AV/UI.
export const selectCurrentBreakoutGroupId = createAppSelector(
  [selectCurrentClassUser, selectIsObservingVirtualClass, selectObserverBreakoutGroupId],
  (classUser, isObserving, observerBreakoutGroupId) => {
    if (classUser?.breakout_group_id) {
      return classUser.breakout_group_id;
    }
    if (isObserving) {
      return observerBreakoutGroupId;
    }
    return null;
  },
);

export const selectCurrentBreakoutGroup = createAppSelector(
  [selectFullBreakoutGroups, selectCurrentBreakoutGroupId],
  (breakoutGroups, breakoutGroupId) =>
    breakoutGroups.find((bg) => bg.id === breakoutGroupId) || null,
);

// ClassUser listen id, else observer-local listen id while observing.
export const selectCurrentListenedBreakoutGroupId = createAppSelector(
  [selectCurrentClassUser, selectIsObservingVirtualClass, selectObserverListenedBreakoutGroupId],
  (classUser, isObserving, observerListenedBreakoutGroupId) => {
    if (classUser?.listened_breakout_group_id) {
      return classUser.listened_breakout_group_id;
    }
    if (isObserving) {
      return observerListenedBreakoutGroupId;
    }
    return null;
  },
);

export const selectCurrentListenedBreakoutGroup = createAppSelector(
  [selectFullBreakoutGroups, selectCurrentListenedBreakoutGroupId],
  (breakoutGroups, listenedBreakoutGroupId) =>
    breakoutGroups.find((bg) => bg.id === listenedBreakoutGroupId) || null,
);

export const selectBreakoutRoomHasRaisedHands = createAppSelector(
  [
    classUserSelectors.filterForCurrentSection,
    (_state, breakoutGroupId: string) => breakoutGroupId,
  ],
  (classUsers, breakoutGroupId): boolean =>
    classUsers.some((cu) => cu.breakout_group_id === breakoutGroupId && !!cu.hand_raised_at),
);

export const selectInstructorClassUserIdsInCurrentBreakoutRoom = createAppSelector(
  [
    classUserSelectors.filterForCurrentSection,
    selectIsClassSessionVirtual,
    selectCurrentBreakoutGroupId,
    selectUserIdsWhoCanAuthorSection,
    selectCurrentClassUserId,
  ],
  (classUsers, isClassSessionVirtual, breakoutGroupId, instructorUserIds, currentClassUserId) => {
    if (!breakoutGroupId || !isClassSessionVirtual) return [];

    const instructorUserIdSet = new Set(instructorUserIds);
    return filterMap(
      classUsers,
      (classUser) =>
        classUser.is_in_class_session &&
        classUser.breakout_group_id === breakoutGroupId &&
        classUser.id !== currentClassUserId &&
        instructorUserIdSet.has(classUser.user_id),
      (classUser) => classUser.id,
    );
  },
);
